import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { EventPayload } from "../../common/types/Event";
import {
  createCustomerCostCenter,
  customerCostCenterById,
  customerCostCentersByOrgIds,
  getCustomerCostCentersByCompanyId,
  getCustomerCostCentersByWorkLocationId,
  updateCustomerCostCenter,
  updateCustomerCostCenterActiveStatus,
  WorkLocationIdFilter,
} from "./customerAPI";
import {
  CreateCustomerCostCenter,
  UpdateActiveStatus,
  UpdateCustomerCostCenter,
} from "./types/commands";
import {
  CustomerCostCenterAdded,
  CustomerCostCenterChanged,
  CustomerCostCenterCreated,
} from "./types/events";
import {
  CustomerCompanyIdFilter,
  CustomerCostCenterDetails,
  CustomerCostCenterDetailsFilter,
  CustomerCostCentersByOrgIdsFilters,
  CustomerCostCentersResult,
} from "./types/queries";
import { createQueryAsyncThunk } from "../../app/createApiAsyncThunk";

const customerCostCentersAdapter = createEntityAdapter<
  CustomerCostCentersResult,
  string
>({
  selectId: (costCenter) => costCenter.customerCCId,
});

type Entities = {
  [key: string]: CustomerCostCentersResult;
};

type SliceState = {
  ids: string[];
  entities: Entities;
  status: "idle" | "pending" | "succeeded" | "failed";
};

export const fetchCustomerCostCenters = createAsyncThunk<
  CustomerCostCentersResult[],
  CustomerCompanyIdFilter,
  { state: RootState }
>(
  "customerCostCenters/fetchCustomerCostCenters",
  async (filters: CustomerCompanyIdFilter, { requestId }) =>
    await getCustomerCostCentersByCompanyId(filters, {
      requestId,
    }),
);

export const fetchCustomerCostCentersByWorkLocationId = createQueryAsyncThunk<
  CustomerCostCentersResult[],
  WorkLocationIdFilter
>(
  "customerCostCenters/fetchCustomerCostCentersByWorkLocationId",
  getCustomerCostCentersByWorkLocationId,
);

export const fetchCustomerCostCenterById = createAsyncThunk<
  CustomerCostCenterDetails,
  CustomerCostCenterDetailsFilter,
  { state: RootState }
>(
  "customerCostCenters/fetchCustomerCostCenterDetails",
  async (filters: CustomerCostCenterDetailsFilter, { requestId }) =>
    await customerCostCenterById(filters, {
      requestId,
    }),
);

export const fetchCustomerCostCentersByOrgIds = createAsyncThunk<
  CustomerCostCenterDetails[],
  CustomerCostCentersByOrgIdsFilters,
  { state: RootState }
>(
  "customerCostCenters/fetchCustomerCostCentersByOrgIds",
  async (filters: CustomerCostCentersByOrgIdsFilters, { requestId }) =>
    await customerCostCentersByOrgIds(filters, {
      requestId,
    }),
);

export const createCostCenter = createAsyncThunk<
  EventPayload<CustomerCostCenterCreated>[],
  CreateCustomerCostCenter,
  { state: RootState }
>(
  "customerCostCenters/addCustomerCostCenter",
  async (costCenter: CreateCustomerCostCenter, { requestId }) =>
    await createCustomerCostCenter(costCenter, {
      requestId,
    }),
);

export const updateCostCenter = createAsyncThunk<
  EventPayload<CustomerCostCenterChanged>[],
  UpdateCustomerCostCenter,
  { state: RootState }
>(
  "customerCostCenters/updateCustomerCostCenter",
  async (costCenter: UpdateCustomerCostCenter, { requestId }) =>
    await updateCustomerCostCenter(costCenter, {
      requestId,
    }),
);

export const updateCostCenterActiveStatus = createAsyncThunk<
  EventPayload<{}>,
  UpdateActiveStatus,
  { state: RootState }
>(
  "customerCostCenters/updateCostCenterActiveStatus",
  async (params: UpdateActiveStatus, { requestId }) =>
    await updateCustomerCostCenterActiveStatus(params, { requestId }),
);

const customerCostCentersSlice = createSlice({
  name: "customerCostCenters",
  initialState: customerCostCentersAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {
    resetCustomerCostCenters: () =>
      customerCostCentersAdapter.getInitialState({
        status: "idle",
      }) as SliceState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCustomerCostCenters.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerCostCenters.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerCostCenters.fulfilled, (state, action) => {
      customerCostCentersAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerCostCenterById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerCostCenterById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerCostCenterById.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchCustomerCostCentersByOrgIds.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCustomerCostCentersByOrgIds.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCustomerCostCentersByOrgIds.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(createCostCenter.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(createCostCenter.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(createCostCenter.fulfilled, (state, action) => {
      action.payload.forEach((event) => {
        if (event.name === "cccAdd") {
          const customerCCId = event.id;
          const { customerOrgId, name } =
            event.payload as CustomerCostCenterAdded;

          customerCostCentersAdapter.addOne(state, {
            customerOrgId,
            name,
            customerCCId,
            active: true,
          });
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(updateCostCenter.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCostCenter.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateCostCenter.fulfilled, (state, action) => {
      action.payload.forEach((event) => {
        if (event.name === "cccNameChg") {
          const { name } = action.meta.arg;
          const customerCCId = event.id;

          customerCostCentersAdapter.updateOne(state, {
            id: customerCCId,
            changes: { name },
          });
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(updateCostCenterActiveStatus.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateCostCenterActiveStatus.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateCostCenterActiveStatus.fulfilled, (state, action) => {
      const { id, version, name } = action.payload;

      const active = name === "cccActivated";
      const changes = { version, active };

      customerCostCentersAdapter.updateOne(state, {
        id,
        changes,
      });

      state.status = "succeeded";
    });
  },
});

export const { resetCustomerCostCenters } = customerCostCentersSlice.actions;

export const { selectAll: selectAllCustomerCostCenters } =
  customerCostCentersAdapter.getSelectors(
    (state: RootState) => state.customerCostCenters,
  );

export const customerCostCentersReducer = customerCostCentersSlice.reducer;
